import moment from "moment";

import {
  CLINIC_ACTIVE,
  CLINIC_FOR_REVIEW,
  EVOKE_PROCESSED,
  EVOKE_ROLES,
  HOSPITAL_NO_MATCH,
  REPORT_INTERVAL_DAYS_MAPPING,
} from "static/constants";
import endpoints from "static/endpoints";
import {
  STORAGE_TO_TRANSMISSION_TABLE_KEY,
  TRANSMISSION_TABLE_ALLOWED_SORTING_COLUMNS,
  TRANSMISSION_TABLE_TO_STORAGE_KEY,
} from "static/transmission_table";
import { changeLocalDateTimeToUTC } from "utils/formatDate";

// Sorting on either of these reads as a flat list, so pairing it with a second column
// would be meaningless. Dropped rather than refused, so the click still does something.
export const NON_MULTI_SORT_COLUMNS = ["name", "assignedPhysician"];

// Columns that filter on a single day, sent to the server as that day's UTC bounds.
export const DAY_RANGE_COLUMN_FILTERS = [
  "date",
  "nextSend",
  "lastBillDate",
  "signedAt",
];

// The Evoke worklists and the clinic worklists are separate endpoints. A clinic role we
// do not list must still land on the clinic one — the Evoke endpoint would refuse it.
export const resolveReportsBasePath = (userRole) =>
  EVOKE_ROLES.includes(userRole)
    ? endpoints.transmission.list
    : `${endpoints.transmission.list}/clinic`;

// The sort order a user left the table in, as the table expresses it.
export const parseStoredSorting = (raw) => {
  let stored;

  try {
    stored = raw ? JSON.parse(raw) : null;
  } catch {
    return [];
  }

  if (!stored || typeof stored !== "object") return [];

  return Object.entries(stored).flatMap(([key, value]) => {
    const id = STORAGE_TO_TRANSMISSION_TABLE_KEY[key] || key;

    if (!TRANSMISSION_TABLE_ALLOWED_SORTING_COLUMNS.includes(id)) return [];

    return [{ id, desc: value === -1 }];
  });
};

// The one place a sort order is settled: drop what the list cannot sort by, apply the
// multi-sort rule, then lead with report notes. Idempotent, so applying it to its own
// result changes nothing.
export const resolveTransmissionSorting = (sorting = []) => {
  const allowed = sorting.filter(({ id }) =>
    TRANSMISSION_TABLE_ALLOWED_SORTING_COLUMNS.includes(id)
  );

  const multiSorted =
    allowed.length > 1
      ? allowed.filter(({ id }) => !NON_MULTI_SORT_COLUMNS.includes(id))
      : allowed;

  const notesIndex = multiSorted.findIndex(({ id }) => id === "reportNotes");

  if (notesIndex <= 0) return multiSorted;

  return [
    multiSorted[notesIndex],
    ...multiSorted.slice(0, notesIndex),
    ...multiSorted.slice(notesIndex + 1),
  ];
};

// Report date always leads, so a saved order restores the way the user saw it.
export const serializeSortingForStorage = (sorting = []) => {
  const sortingObject = sorting.reduce((acc, { id, desc }) => {
    const storageKey = TRANSMISSION_TABLE_TO_STORAGE_KEY[id];

    if (!TRANSMISSION_TABLE_ALLOWED_SORTING_COLUMNS.includes(id)) return acc;
    if (!storageKey) return acc;

    acc[storageKey] = desc ? -1 : 1;
    return acc;
  }, {});

  if (Object.keys(sortingObject).length === 0) return null;

  return { report_date: -1, ...sortingObject };
};

// A date column filter picks its own window, so the interval selector would fight it.
export const shouldUseReportIntervalFilter = (columnFilters = []) =>
  !columnFilters.some((filter) => filter?.id === "date");

// The physicians a user follows, as saved for them.
export const resolveFollowingPhysicians = (raw) =>
  String(raw ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

// An assigned hospital list belongs to the user, and any role may hold one.
export const resolveFollowingHospitals = (hospitals, isFollowingHospital) => {
  if (isFollowingHospital !== true) return "";

  const list = Array.isArray(hospitals)
    ? hospitals
    : String(hospitals ?? "").split(",");

  return [
    ...new Set(list.map((name) => String(name ?? "").trim()).filter(Boolean)),
  ].join(",");
};

// A clinician's own patients, on the review queue only: because the account requires it,
// or because that is where they last left the toggle. The profile preference gates the
// remembered choice — without it the queue opens unfiltered whatever was last chosen.
export const resolveMyPatientsFilter = ({
  view,
  showOnlyAssignedReports,
  myPatientsPreference,
  storedMyPatientsToggled,
} = {}) => {
  if (view !== CLINIC_FOR_REVIEW) return false;
  if (showOnlyAssignedReports) return true;
  if (!myPatientsPreference) return false;

  return storedMyPatientsToggled === null || storedMyPatientsToggled === "true";
};

// A day filter covers that whole day, in the timezone the user is filtering from.
const buildDayRange = (value) => {
  const start = changeLocalDateTimeToUTC(value, "YYYY-MM-DDTHH:mm:ss");
  const end = changeLocalDateTimeToUTC(
    moment(new Date(value)).add(1, "days"),
    "YYYY-MM-DDTHH:mm:ss"
  );

  return `${start},${end}`;
};

export const buildColumnFilterQuery = (columnFilters = []) =>
  columnFilters
    .map(({ id, value }) =>
      DAY_RANGE_COLUMN_FILTERS.includes(id)
        ? `${id}=${buildDayRange(value)}&`
        : `${id}=${value}&`
    )
    .filter(Boolean)
    .join("");

export const encodeHospitals = (hospitals) =>
  (Array.isArray(hospitals) ? hospitals : String(hospitals ?? "").split(","))
    .filter(Boolean)
    .map(encodeURIComponent)
    .join(",");

const normaliseHospital = (hospital) =>
  String(hospital ?? "")
    .trim()
    .toLowerCase();

// The server applies only one hospital list, so the column filter narrows the assigned one.
export const resolveRequestedHospitals = (
  followingHospitals,
  hospitalFilter
) => {
  const assigned = String(followingHospitals ?? "")
    .split(",")
    .filter(Boolean);

  const picked = Array.isArray(hospitalFilter)
    ? hospitalFilter.map(normaliseHospital).filter(Boolean)
    : null;
  const typed = picked ? "" : normaliseHospital(hospitalFilter);

  if (!picked?.length && !typed) return assigned.join(",");

  const narrowed = assigned.filter((hospital) =>
    picked
      ? picked.includes(normaliseHospital(hospital))
      : normaliseHospital(hospital).includes(typed)
  );

  return narrowed.length ? narrowed.join(",") : HOSPITAL_NO_MATCH;
};

// Everything the user has switched on, as the server expects to receive it.
export const buildTransmissionFilterQuery = ({
  view,
  physiciansFilter = [],
  reportType = "all",
  isFilterAlert = false,
  isHideBilledFilter = false,
  isMyPatientsFilter = false,
  transmissionType = "all",
  priorityType = "all",
  showSavedReports = false,
  showAuditedReports = false,
  columnFilters = [],
  followingHospitals = "",
} = {}) => {
  const toggles = [
    physiciansFilter?.length > 0 && `physicians=${physiciansFilter}&`,
    reportType !== "all" && `reportType=${reportType}&`,
    isFilterAlert && `isAlertFilter=${isFilterAlert}&`,
    isHideBilledFilter && `isHideBilledFilter=${isHideBilledFilter}&`,
    view === CLINIC_FOR_REVIEW &&
      isMyPatientsFilter &&
      `isMyPatientsFilter=${isMyPatientsFilter}&`,
    transmissionType !== "all" && `transmissionType=${transmissionType}&`,
    priorityType !== "all" && priorityType && `priorityType=${priorityType}&`,
    showSavedReports && `saveForLaterFilter=${showSavedReports}&`,
    showAuditedReports && `auditedReportsFilter=${showAuditedReports}&`,
  ]
    .filter(Boolean)
    .join("");

  if (!followingHospitals) {
    return `${toggles}${buildColumnFilterQuery(columnFilters)}`;
  }

  const hospitalFilter = columnFilters.find(({ id }) => id === "hospital");
  const otherColumnFilters = columnFilters.filter(
    ({ id }) => id !== "hospital"
  );
  const hospitals = resolveRequestedHospitals(
    followingHospitals,
    hospitalFilter?.value
  );

  return `${toggles}${buildColumnFilterQuery(
    otherColumnFilters
  )}followingHospitals=${encodeHospitals(hospitals)}&`;
};

// The whole request, decided in one place. Two states that would ask the server the same
// question produce the same string, so they cost one request between them.
export const buildTransmissionReportsUrl = ({
  userRole,
  view,
  page = 0,
  pageSize = 50,
  search,
  filters = "",
  sorting = [],
  reportId,
  year,
  refresh,
  reportInterval,
  useReportIntervalFilter,
  patch,
} = {}) => {
  let url = `${resolveReportsBasePath(userRole)}?`;

  if (view && view !== CLINIC_ACTIVE) url += `view=${view}&`;

  url += String(filters ?? "");

  (sorting ?? []).forEach(({ id, desc }) => {
    url += `sort=${id}&order=${desc ? "desc" : "asc"}&`;
  });

  if (search) url += `search=${search}&`;
  if (refresh) url += `refresh=${refresh}&`;
  if (reportId) url += `reportId=${reportId}&`;
  if (year) url += `year=${year}&`;

  // Only the patch worklist sets this; every other request URL is unchanged.
  if (patch) url += "patch=true&";

  if (view === EVOKE_PROCESSED && useReportIntervalFilter && reportInterval) {
    url += `reportIntervalDays=${REPORT_INTERVAL_DAYS_MAPPING[reportInterval]}&`;
  }

  return `${url}page=${page}&pageSize=${pageSize}`;
};
